/**
 * サイドバー拡張機能
 * - 料金明細の表示
 * - 発送予定日の日付形式表示
 * - 見積書ダウンロード機能
 */

class SidebarEnhancements {
  constructor() {
    
    this.init();
  }

  init() {
    // 価格計算器の更新を監視
    this.observePriceUpdates();
    
    // 見積書ダウンロードボタンのイベントリスナー
    this.initEstimateModal();
  }

  /**
   * 価格更新を監視して料金明細と発送予定日を更新
   */
  observePriceUpdates() {
    const self = this;
    
    // DynamicPriceCalculatorの更新イベントを監視
    const checkAndUpdate = () => {
      if (window.priceCalculator && window.priceCalculator.currentSelection) {
        self.updatePriceDetail();
        self.updateDeliveryDate(); // フッターのみ更新
      }
    };

    // 初回実行（遅延を長めに）
    setTimeout(checkAndUpdate, 1500);

    // 定期的にチェック（価格表のセルがクリックされた時など）
    document.addEventListener('click', (e) => {
      // 価格セルのクリック
      if (e.target.closest('.price-cell') || e.target.closest('[data-price]') || 
          e.target.closest('.price-table-cell') || e.target.closest('td[data-quantity]')) {
        setTimeout(checkAndUpdate, 100);
      }
    });
    
    // オプションモーダルの「選択」ボタンクリック時に更新
    document.addEventListener('click', (e) => {
      if (e.target.closest('.grouped-options-confirm-btn') || 
          e.target.closest('[data-action="confirm-options"]') ||
          e.target.id === 'grouped-options-confirm') {
        setTimeout(checkAndUpdate, 200);
      }
    });
    
    // オプション選択完了イベントで更新（GroupedOptionsManagerからdispatchされる）
    document.addEventListener('optionsUpdated', (e) => {
      setTimeout(checkAndUpdate, 100);
    });

    // セレクターの変更を監視
    document.addEventListener('change', (e) => {
      if (e.target.closest('#quantity-select') || 
          e.target.closest('#quantity-input') ||  // 左カラムの部数セレクター
          e.target.closest('#delivery-select') ||
          e.target.closest('#paper-select') ||
          e.target.closest('#print-color-select') ||
          e.target.closest('.price-selector') ||
          e.target.closest('.mobile-selector') ||
          e.target.closest('.nav-select')) {  // 左カラムの全セレクター
        setTimeout(checkAndUpdate, 100);
      }
    });

    // カスタムイベントを監視（オプション選択時に発火）
    document.addEventListener('optionsUpdated', () => {
      setTimeout(checkAndUpdate, 100);
    });
    
    // priceCalculatorUpdateイベントも監視
    document.addEventListener('priceCalculatorUpdate', () => {
      setTimeout(checkAndUpdate, 100);
    });
    
    // priceUpdateイベントも監視（DynamicPriceCalculatorから発火）
    document.addEventListener('priceUpdate', (e) => {
      setTimeout(checkAndUpdate, 100);
    });

    // オプション選択時にも更新（後からロードされる場合に対応）
    const setupOptionsManagerHook = () => {
      if (window.productOptionsManager && window.productOptionsManager.recalculatePrice) {
        const originalRecalculate = window.productOptionsManager.recalculatePrice.bind(window.productOptionsManager);
        window.productOptionsManager.recalculatePrice = function(...args) {
          const result = originalRecalculate(...args);
          setTimeout(checkAndUpdate, 100);
          return result;
        };
      } else {
        // まだロードされていない場合は再試行
        setTimeout(setupOptionsManagerHook, 500);
      }
    };
    setupOptionsManagerHook();
    
    // DynamicPriceCalculatorのupdateUIをフック
    const setupPriceCalculatorHook = () => {
      if (window.priceCalculator && window.priceCalculator.updateUI) {
        const originalUpdateUI = window.priceCalculator.updateUI.bind(window.priceCalculator);
        window.priceCalculator.updateUI = function(...args) {
          const result = originalUpdateUI(...args);
          setTimeout(checkAndUpdate, 50);
          return result;
        };
      } else {
        // まだロードされていない場合は再試行
        setTimeout(setupPriceCalculatorHook, 500);
      }
    };
    setupPriceCalculatorHook();
  }

  /**
   * 料金明細を更新
   */
  updatePriceDetail() {
    if (!window.priceCalculator) return;

    const result = window.priceCalculator.calculatePrice();
    if (!result) return;

    // オプション価格調整を取得
    let optionPrice = 0;
    if (window.productOptionsManager && typeof window.productOptionsManager.getOptionAdjustments === 'function') {
      const adjustments = window.productOptionsManager.getOptionAdjustments();
      if (adjustments && typeof adjustments.price === 'number') {
        optionPrice = adjustments.price;
      }
    }

    const productPrice = result.basePrice;
    const totalPrice = result.total;

    // サイドバーの料金明細を更新
    const productPriceEl = document.getElementById('sidebar-product-price');
    const optionPriceEl = document.getElementById('sidebar-option-price');
    const totalPriceEl = document.getElementById('sidebar-total-price');

    if (productPriceEl) {
      productPriceEl.classList.remove('skeleton-loading');
      productPriceEl.textContent = productPrice.toLocaleString();
    }
    if (optionPriceEl) {
      optionPriceEl.classList.remove('skeleton-loading');
      optionPriceEl.textContent = optionPrice.toLocaleString();
    }
    if (totalPriceEl) {
      totalPriceEl.classList.remove('skeleton-loading');
      totalPriceEl.textContent = totalPrice.toLocaleString();
    }

  }

  /**
   * 発送予定日を日付形式で更新（フッターのみ）
   */
  updateDeliveryDate() {
    if (!window.priceCalculator || !window.priceCalculator.currentSelection) return;

    const rawDelivery = window.priceCalculator.currentSelection.delivery;
    
    // 商品の発送日計算方式を取得
    const productCalcType = window.productDeliveryCalcType || 'business_days';
    
    // 納期を正しい形式に変換（価格表と同じ計算になるように）
    let delivery = rawDelivery;
    if (productCalcType === 'business_days' && rawDelivery && !rawDelivery.includes('営業日')) {
      // 営業日計算の場合：「1日」→「1営業日」
      const dayMatch = rawDelivery.match(/(\d+)日/);
      if (dayMatch) {
        delivery = dayMatch[1] + '営業日';
      }
    } else if (productCalcType === 'calendar_days' && rawDelivery && rawDelivery.includes('営業日')) {
      // 暦日計算の場合：「1営業日」→「1日」
      const businessMatch = rawDelivery.match(/(\d+)営業日/);
      if (businessMatch) {
        delivery = businessMatch[1] + '日';
      }
    }
    
    // オプションによる納期調整を取得
    let leadTimeAdjustment = 0;
    if (window.productOptionsManager && typeof window.productOptionsManager.getOptionAdjustments === 'function') {
      const adjustments = window.productOptionsManager.getOptionAdjustments();
      if (adjustments && typeof adjustments.leadTime === 'number') {
        leadTimeAdjustment = adjustments.leadTime;
      }
    }

    const deliveryDate = this.calculateDeliveryDate(delivery, leadTimeAdjustment);

    // フッターの発送予定日を更新
    const footerDeliveryEl = document.getElementById('footer-delivery');
    if (footerDeliveryEl) {
      footerDeliveryEl.textContent = deliveryDate.formatted;
    }

  }

  /**
   * 納期から発送予定日を計算
   * BusinessDayCalculatorを使用（祝日・会社休業日を考慮）
   * @param {string} delivery - 納期文字列（例: "3営業日", "3日"）
   * @param {number} leadTimeAdjustment - 追加の日数調整
   * @param {object} options - オプション { calcType: 'business_days' | 'calendar_days' }
   */
  calculateDeliveryDate(delivery, leadTimeAdjustment = 0, options = {}) {
    // 商品の計算タイプをoptionsに渡す（指定がなければwindowから取得）
    const calcType = options.calcType || window.productDeliveryCalcType || 'business_days';

    // BusinessDayCalculatorが利用可能な場合はそちらを使用
    if (window.businessDayCalculator && window.businessDayCalculator.loaded) {
      const result = window.businessDayCalculator.calculateDeliveryDate(delivery, leadTimeAdjustment, { calcType });
      return {
        date: result.date,
        formatted: result.formatted
      };
    }
    
    // フォールバック（従来の計算）
    let days = 0;
    let useBusinessDays = true;

    if (delivery.includes('当日')) {
      days = 0;
    } else {
      const businessMatch = delivery.match(/(\d+)営業日/);
      if (businessMatch) {
        days = parseInt(businessMatch[1], 10);
        useBusinessDays = true;
      } else {
        // 「X日」形式の場合
        const calendarMatch = delivery.match(/(\d+)日/);
        if (calendarMatch) {
          days = parseInt(calendarMatch[1], 10);
          useBusinessDays = calcType !== 'calendar_days';
        }
      }
    }

    days += leadTimeAdjustment;

    const today = new Date();
    let targetDate;
    if (useBusinessDays) {
      targetDate = this.addBusinessDays(today, days);
    } else {
      // 暦日計算（単純加算）
      targetDate = new Date(today);
      targetDate.setDate(targetDate.getDate() + days);
    }

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    const weekday = weekdays[targetDate.getDay()];

    return {
      date: targetDate,
      formatted: `${year}/${month}/${day}(${weekday})`
    };
  }

  /**
   * 営業日を加算（土日を除く）
   * BusinessDayCalculatorが利用可能な場合はそちらを使用
   */
  addBusinessDays(startDate, days) {
    // BusinessDayCalculatorが利用可能な場合はそちらを使用
    if (window.businessDayCalculator && window.businessDayCalculator.loaded) {
      return window.businessDayCalculator.addBusinessDays(startDate, days);
    }
    
    // フォールバック（従来の計算）
    const result = new Date(startDate);
    let daysToAdd = days;

    while (daysToAdd > 0) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        daysToAdd--;
      }
    }

    return result;
  }

  /**
   * 見積書ダウンロードモーダルの初期化
   */
  initEstimateModal() {
    const downloadBtn = document.getElementById('estimate-download-btn');
    const estimateSection = document.getElementById('estimate-section');
    const modalOverlay = document.getElementById('estimate-modal-overlay');
    const closeBtn = document.getElementById('estimate-modal-close');
    const cancelBtn = document.getElementById('estimate-modal-cancel');
    const confirmBtn = document.getElementById('estimate-modal-download');

    // 会社情報が設定されていない場合は見積書ボタンを非表示
    if (!window.companyInfoConfigured) {
      if (estimateSection) {
        estimateSection.style.display = 'none';
      }
      console.warn('[SidebarEnhancements] 会社情報が未設定のため、見積書ダウンロードボタンを非表示にしました');
      return;
    }

    if (!downloadBtn || !modalOverlay) {
      return;
    }

    // モーダルを開く
    downloadBtn.addEventListener('click', () => {
      modalOverlay.style.display = 'flex';
    });

    // モーダルを閉じる
    const closeModal = () => {
      modalOverlay.style.display = 'none';
    };

    closeBtn.addEventListener('click', closeModal);
    cancelBtn.addEventListener('click', closeModal);
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        closeModal();
      }
    });

    // ダウンロードボタン
    confirmBtn.addEventListener('click', () => {
      const name = document.getElementById('estimate-name').value.trim();
      const title = document.getElementById('estimate-title').value.trim();
      const recipientType = document.querySelector('input[name="estimate-recipient-type"]:checked')?.value || 'corporate';

      if (!name || !title) {
        alert('見積書宛名とタイトルを入力してください。');
        return;
      }

      try {
        this.generateEstimatePDF(name, title, recipientType);
      } catch (error) {
        console.error('[SidebarEnhancements] Error in generateEstimatePDF:', error);
        alert('見積書の生成に失敗しました。\n' + error.message);
      }
      // モーダルは必ず閉じる（generateEstimatePDFの成否に関わらず）
      closeModal();
    });

  }

  /**
   * 見積書PDFを生成
   * @param {string} customerName - 宛名
   * @param {string} estimateTitle - 見積書タイトル
   * @param {string} recipientType - 宛名種別 ('corporate' | 'individual')
   */
  generateEstimatePDF(customerName, estimateTitle, recipientType = 'corporate') {

    if (!window.priceCalculator || !window.priceCalculator.currentSelection) {
      alert('価格情報が取得できません。');
      return;
    }

    const result = window.priceCalculator.calculatePrice();
    if (!result) {
      alert('価格情報が取得できません。');
      return;
    }

    // オプション価格調整を取得
    let optionPrice = 0;
    if (window.productOptionsManager && typeof window.productOptionsManager.getOptionAdjustments === 'function') {
      const adjustments = window.productOptionsManager.getOptionAdjustments();
      if (adjustments && typeof adjustments.price === 'number') {
        optionPrice = adjustments.price;
      }
    }

    const productInfo = window.productData;
    const selection = window.priceCalculator.currentSelection;

    // PDF生成前にUI表示値とcurrentSelectionを同期（リセット対策）
    this.syncSelectionFromUI(selection);

    // ProductDisplayUtilsから商品仕様プロパティを取得（ヘッダー表示と同じロジック）
    const specProperties = (window.ProductDisplayUtils && typeof window.ProductDisplayUtils.getOrderProperties === 'function')
      ? window.ProductDisplayUtils.getOrderProperties()
      : [];

    console.log('[SidebarEnhancements] Estimate PDF - selection:', {
      bodyPaper: selection.bodyPaper,
      coverPaper: selection.coverPaper,
      unifiedPaper: selection.unifiedPaper,
      pageCount: selection.pageCount,
      pageRange: selection.pageRange
    });
    console.log('[SidebarEnhancements] Estimate PDF - specProperties:', specProperties);

    // 見積書データを作成
    const estimateData = {
      customerName: customerName,
      title: estimateTitle,
      recipientType: recipientType,
      date: new Date().toLocaleDateString('ja-JP'),
      productName: productInfo.title,
      quantity: selection.quantity,
      delivery: selection.delivery,
      specProperties: specProperties,
      basePrice: result.basePrice,
      optionPrice: optionPrice,
      totalPrice: result.total
    };

    // PDF生成APIを呼び出し（実装は後ほど）
    this.downloadEstimate(estimateData);
  }

  /**
   * 見積書をダウンロード（HTML→Canvas→PDF方式で日本語対応）
   */
  async downloadEstimate(data) {
    let tempContainer = null;
    try {
      // jsPDFとhtml2canvasがロードされているか確認
      if (!window.jspdf || !window.jspdf.jsPDF) {
        throw new Error('PDF生成ライブラリがロードされていません。');
      }

      if (!window.html2canvas) {
        throw new Error('html2canvas ライブラリがロードされていません。');
      }

      const { jsPDF } = window.jspdf;

      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const dateStr = `${year}年${month}月${day}日`;

      // 見積書HTMLを動的に生成
      const estimateHtml = this.generateEstimateHTML(data, dateStr);

      // 一時的にDOMに追加（レンダリング用）
      tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '210mm'; // A4幅
      tempContainer.style.background = '#ffffff';
      tempContainer.innerHTML = estimateHtml;
      document.body.appendChild(tempContainer);

      // html2canvasでHTMLをキャンバスに変換
      const canvas = await window.html2canvas(tempContainer, {
        scale: 2, // 高解像度
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // DOMから削除
      document.body.removeChild(tempContainer);
      tempContainer = null;

      // PDFを作成
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // キャンバスをPDFに追加
      const imgData = canvas.toDataURL('image/png');
      const pdfWidth = 210; // A4幅（mm）
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, Math.min(pdfHeight, 297)); // A4高さまで

      // PDFをダウンロード
      const fileName = `mitumori_${year}${month}${day}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('[SidebarEnhancements] Error downloading estimate:', error);
      alert('見積書のダウンロードに失敗しました。\n' + error.message);
    } finally {
      // tempContainerがDOMに残っている場合は必ず削除
      if (tempContainer && tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
    }
  }

  /**
   * 見積書HTMLを生成
   */
  generateEstimateHTML(data, dateStr) {
    // 会社情報を取得（フォールバックなし - 必須）
    const companyInfo = window.companyInfo;
    if (!companyInfo) {
      throw new Error('会社情報が設定されていません。管理画面から会社情報を設定してください。');
    }

    // 選択されたオプション情報を取得
    let optionDetails = '';
    if (window.productOptionsManager) {
      const requiredOptions = window.productOptionsManager.selectedOptions.required || {};
      const optionalOptions = window.productOptionsManager.selectedOptions.optional || {};

      for (const key in requiredOptions) {
        if (requiredOptions.hasOwnProperty(key)) {
          const option = requiredOptions[key];
          optionDetails += `<div style="padding: 3px 0; font-size: 9px;">${key}: ${option.label || option.value}</div>`;
        }
      }
      for (const key in optionalOptions) {
        if (optionalOptions.hasOwnProperty(key)) {
          const option = optionalOptions[key];
          optionDetails += `<div style="padding: 3px 0; font-size: 9px;">${key}: ${option.label || option.value}</div>`;
        }
      }
    }

    // ロゴHTMLを生成
    let logoHtml = '';
    if (companyInfo.logo) {
      logoHtml = `<div style="margin-bottom: 5px;"><img src="${companyInfo.logo}" style="max-width: 100px; height: auto;" alt="Company Logo" /></div>`;
    }

    // 会社情報HTMLを生成（改行で分割）
    let detailsText = '';
    if (typeof companyInfo.details === 'string') {
      detailsText = companyInfo.details;
    } else if (Array.isArray(companyInfo.details)) {
      detailsText = companyInfo.details.join('\n');
    }
    const detailsLines = detailsText.split('\n').filter(line => line.trim());
    const detailsHtml = detailsLines.map(line =>
      `<div style="margin-bottom: 3px;">${line.trim()}</div>`
    ).join('');

    // 注意事項HTMLを生成（改行で分割）
    let notesText = '';
    if (typeof companyInfo.notes === 'string') {
      notesText = companyInfo.notes;
    } else if (Array.isArray(companyInfo.notes)) {
      notesText = companyInfo.notes.join('\n');
    }
    const notesLines = notesText.split('\n').filter(line => line.trim());
    const notesHtml = notesLines.map(note =>
      `<div>${note.trim()}</div>`
    ).join('');

    return `
      <div style="
        width: 210mm;
        min-height: 297mm;
        padding: 20mm 15mm;
        background: #ffffff;
        font-family: 'メイリオ', 'Meiryo', 'ヒラギノ角ゴ Pro W3', 'Hiragino Kaku Gothic Pro', sans-serif;
        color: #000000;
        position: relative;
      ">
        <!-- ページ番号 -->
        <div style="position: absolute; top: 10mm; right: 15mm; font-size: 9px;">(1/1)</div>

        <!-- タイトル -->
        <div style="text-align: center; margin-bottom: 15mm;">
          <h1 style="
            font-size: 22px;
            margin: 0 0 5px 0;
            padding-bottom: 5px;
            border-bottom: 2px solid #000;
            display: inline-block;
            min-width: 200px;
            font-weight: bold;
          ">御 見 積 書</h1>
        </div>

        <!-- ヘッダー情報 -->
        <div style="display: flex; justify-content: space-between; margin-bottom: 5mm;">
          <!-- 左側：お客様名 -->
          <div style="flex: 1;">
            <div style="font-size: 14px; font-weight: bold; margin-bottom: 5mm;">
              ${data.customerName} ${data.recipientType === 'individual' ? '様' : '御中'}
            </div>
            <div style="font-size: 10px; margin-top: 3mm;">
              下記の通り御見積申し上げます。
            </div>
          </div>

          <!-- 右側：会社情報 -->
          <div style="text-align: right; font-size: 9px; line-height: 1.6;">
            ${logoHtml}
            <div style="margin-bottom: 3px; font-weight: bold;">発行: ${dateStr}</div>
            ${detailsHtml}
          </div>
        </div>

        <!-- 合計金額ボックス（テーブルの右上に配置） -->
        <div style="
          float: right;
          width: 50mm;
          border: 1px solid #999;
          background: #f5f5f5;
          padding: 5mm;
          text-align: center;
          margin-bottom: 5mm;
          margin-left: 5mm;
        ">
          <div style="font-size: 10px; margin-bottom: 3mm; font-weight: bold;">合計金額(税込)</div>
          <div style="font-size: 16px; font-weight: bold;">${data.totalPrice.toLocaleString()}</div>
        </div>

        <!-- 商品詳細テーブル -->
        <table style="
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
          clear: both;
        ">
          <!-- テーブルヘッダー -->
          <thead>
            <tr style="background: #e0e0e0;">
              <th style="border: 1px solid #999; padding: 5px; width: 15mm; text-align: center;"></th>
              <th style="border: 1px solid #999; padding: 5px; text-align: center;">商品</th>
              <th style="border: 1px solid #999; padding: 5px; width: 30mm; text-align: center;">部数</th>
              <th style="border: 1px solid #999; padding: 5px; width: 35mm; text-align: center;">金額</th>
            </tr>
          </thead>
          <!-- テーブルボディ -->
          <tbody>
            <tr>
              <td style="border: 1px solid #999; padding: 8px; text-align: center; vertical-align: top;">1</td>
              <td style="border: 1px solid #999; padding: 8px; vertical-align: top;">
                <div style="font-weight: bold; margin-bottom: 5px;">${data.title || data.productName}</div>
                <div style="font-size: 9px; line-height: 1.6;">
                  <div>【${data.productName}】</div>
                  ${(data.specProperties || []).map(prop => `<div>${prop.name}: ${prop.value}</div>`).join('')}
                  ${data.delivery ? `<div>納期: ${data.delivery}</div>` : ''}
                  ${optionDetails}
                </div>
              </td>
              <td style="border: 1px solid #999; padding: 8px; text-align: right; vertical-align: top;">
                ${data.quantity.toLocaleString()}
              </td>
              <td style="border: 1px solid #999; padding: 8px; text-align: right; vertical-align: top;">
                ${data.totalPrice.toLocaleString()}
              </td>
            </tr>
            <!-- 小計行 -->
            <tr style="background: #f5f5f5;">
              <td colspan="3" style="border: 1px solid #999; padding: 8px; text-align: right; font-weight: bold;">
                小計
              </td>
              <td style="border: 1px solid #999; padding: 8px; text-align: right; font-weight: bold;">
                ${data.totalPrice.toLocaleString()}
              </td>
            </tr>
            <!-- 合計行 -->
            <tr style="background: #e0e0e0;">
              <td colspan="3" style="border: 1px solid #999; padding: 8px; text-align: right; font-weight: bold;">
                合計
              </td>
              <td style="border: 1px solid #999; padding: 8px; text-align: right; font-weight: bold;">
                ${data.totalPrice.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- フッター（注意事項） -->
        <div style="
          position: absolute;
          bottom: 15mm;
          left: 15mm;
          right: 15mm;
          font-size: 7px;
          line-height: 1.8;
          color: #666;
        ">
          ${notesHtml}
        </div>
      </div>
    `;
  }

  /**
   * UI表示値からcurrentSelectionを同期
   * loadPriceMatrix等でcurrentSelectionがリセットされた場合の対策
   * @param {Object} selection - currentSelection
   */
  syncSelectionFromUI(selection) {
    // プレースホルダー値は無視する
    const isPlaceholder = (val) => !val || val === '---' || val === '選択してください';

    // 本文紙質
    const bodyPaperEl = document.getElementById('body-paper-current-value');
    if (bodyPaperEl && bodyPaperEl.textContent.trim()) {
      const uiBodyPaper = bodyPaperEl.textContent.trim();
      if (!isPlaceholder(uiBodyPaper) && uiBodyPaper !== selection.bodyPaper) {
        console.log('[SidebarEnhancements] syncSelectionFromUI - bodyPaper mismatch:', {
          ui: uiBodyPaper, selection: selection.bodyPaper
        });
        selection.bodyPaper = uiBodyPaper;
      }
    }

    // 表紙紙質
    const coverPaperEl = document.getElementById('cover-paper-current-value');
    if (coverPaperEl && coverPaperEl.textContent.trim()) {
      const uiCoverPaper = coverPaperEl.textContent.trim();
      if (!isPlaceholder(uiCoverPaper) && uiCoverPaper !== selection.coverPaper) {
        console.log('[SidebarEnhancements] syncSelectionFromUI - coverPaper mismatch:', {
          ui: uiCoverPaper, selection: selection.coverPaper
        });
        selection.coverPaper = uiCoverPaper;
      }
    }

    // 統合紙セレクター（印刷用紙）
    const paperEl = document.getElementById('paper-current-value');
    if (paperEl && paperEl.textContent.trim() && window.productData?.isUnifiedPaperMode) {
      const uiPaper = paperEl.textContent.trim();
      if (!isPlaceholder(uiPaper) && uiPaper !== selection.unifiedPaper) {
        console.log('[SidebarEnhancements] syncSelectionFromUI - unifiedPaper mismatch:', {
          ui: uiPaper, selection: selection.unifiedPaper
        });
        selection.unifiedPaper = uiPaper;
      }
    }

    // ページ数
    const pageCountEl = document.getElementById('page-count-current-value');
    if (pageCountEl && pageCountEl.textContent.trim()) {
      const match = pageCountEl.textContent.match(/(\d+)/);
      if (match) {
        const uiPageCount = parseInt(match[1]);
        if (uiPageCount && uiPageCount !== selection.pageCount) {
          console.log('[SidebarEnhancements] syncSelectionFromUI - pageCount mismatch:', {
            ui: uiPageCount, selection: selection.pageCount
          });
          selection.pageCount = uiPageCount;
        }
      }
    }
  }

  /**
   * 日本語テキストをエンコード（簡易版）- 不要になったが後方互換性のため残す
   */
  encodeJapanese(text) {
    return text;
  }

  /**
   * 手動でテーブルを描画（autoTableが利用できない場合）- 不要になったが後方互換性のため残す
   */
  drawManualTable(pdf, startY, headers, data, totalPrice) {
    // 旧実装は使用しない
    console.warn('[SidebarEnhancements] drawManualTable is deprecated');
  }
}

// ページ読み込み後に初期化
document.addEventListener('DOMContentLoaded', () => {
  window.sidebarEnhancements = new SidebarEnhancements();
});


